import { CertificateCategory, CertificateStatus } from "@tfg/db"

import {
  FindProductsProductStandardCertificateDto,
  FindProductsProductStandardDto,
} from "~/gen/api"
import { standardRulesTranslations } from "~/pages/Brand/Certificates/type.ts"

export function certificateIsVerifiedWithOldRules(certificate: {
  status: CertificateStatus | null
  certificationBody: string | null
  certifiedOrganizationName: string | null
  certifiedOrganizationAddress: string | null
  certifiedOrganizationLicenseNumber: string | null
  certifiedWeight: string | null
  linkedScopeCertificateNumber: string | null
  processingSteps: string[] | null
  productDetails: string[] | null
  productCategories: string[] | null
}): boolean {
  return (
    certificate.status === CertificateStatus.PROCESSED &&
    certificate.certificationBody === null &&
    certificate.certifiedOrganizationName === null &&
    certificate.certifiedOrganizationAddress === null &&
    certificate.certifiedOrganizationLicenseNumber === null &&
    certificate.certifiedWeight === null &&
    certificate.linkedScopeCertificateNumber === null &&
    (certificate.processingSteps === null ||
      certificate.processingSteps?.length === 0) &&
    (certificate.productDetails === null ||
      certificate.productDetails?.length === 0) &&
    (certificate.productCategories === null ||
      certificate.productCategories?.length === 0)
  )
}

function standardHasOldCertificateVerified(
  standard: FindProductsProductStandardDto
) {
  return standard.certificates.some((certificate) => {
    return certificateIsVerifiedWithOldRules(certificate)
  })
}

export function standardRequiresDeepCompliance(
  standard: FindProductsProductStandardDto
): boolean {
  if (standardHasOldCertificateVerified(standard)) return false
  if (
    standard.certificates.length == 2 &&
    standard.certificates.some(
      (certificate) =>
        certificate.type.category === CertificateCategory.TRANSACTION
    ) &&
    standard.certificates.some(
      (certificate) => certificate.type.category === CertificateCategory.SCOPE
    )
  )
    return true
  return false
}

export function isCertificateVerified(
  certificate: FindProductsProductStandardCertificateDto
) {
  return certificate.status === CertificateStatus.PROCESSED
}

export function isStandardCompliant(standard: FindProductsProductStandardDto) {
  if (!standardRequiresDeepCompliance(standard))
    return isStandardLightCompliant(standard)

  return isStandardDeeplyCompliant(standard)
}

export function isStandardDeeplyCompliant(
  standard: FindProductsProductStandardDto
) {
  const transactionCertificate = standard.certificates.find(
    (certificate) =>
      certificate.type.category === CertificateCategory.TRANSACTION &&
      certificate.status === CertificateStatus.PROCESSED
  )
  const scopeCertificate = standard.certificates.find(
    (certificate) =>
      certificate.type.category === CertificateCategory.SCOPE &&
      certificate.status === CertificateStatus.PROCESSED
  )

  if (!transactionCertificate || !scopeCertificate) return false

  return getComplianceRules(transactionCertificate, scopeCertificate).every(
    (rule) => rule.isCompliant
  )
}

function isStandardLightCompliant(standard: FindProductsProductStandardDto) {
  const completedCertificates = standard.certificates.filter(
    (certificate) => certificate.status === CertificateStatus.PROCESSED
  ).length
  return (
    completedCertificates > 0 &&
    completedCertificates === standard.certificates.length
  )
}

function tcIssueDateInScValidityPeriod(
  tc: FindProductsProductStandardCertificateDto,
  sc: FindProductsProductStandardCertificateDto
) {
  return (
    tc.issueDate &&
    sc.issueDate &&
    sc.expiryDate &&
    tc.issueDate >= sc.issueDate &&
    tc.issueDate <= sc.expiryDate
  )
}

function certificatesOnSameLegalEntity(
  tc: FindProductsProductStandardCertificateDto,
  sc: FindProductsProductStandardCertificateDto
) {
  if (!tc.certifiedOrganizationName || !sc.certifiedOrganizationName) {
    return false
  }

  return tc.certifiedOrganizationName === sc.certifiedOrganizationName
}

function tcProductCategoryInScProductCategories(
  tc: FindProductsProductStandardCertificateDto,
  sc: FindProductsProductStandardCertificateDto
) {
  if (
    !tc.productCategories ||
    !sc.productCategories ||
    tc.productCategories?.length === 0 ||
    sc.productCategories?.length === 0
  ) {
    return false
  }

  return tc.productCategories.every((cat) =>
    sc.productCategories?.includes(cat)
  )
}

function tcAndScFromSameCertificationBody(
  tc: FindProductsProductStandardCertificateDto,
  sc: FindProductsProductStandardCertificateDto
) {
  return (
    tc.certificationBody &&
    sc.certificationBody &&
    tc.certificationBody === sc.certificationBody
  )
}

export const getComplianceRules = (
  transactionCertificate: FindProductsProductStandardCertificateDto,
  scopeCertificate: FindProductsProductStandardCertificateDto
): {
  name: keyof typeof standardRulesTranslations
  description: keyof typeof standardRulesTranslations
  isCompliant: boolean
}[] => [
  {
    name: "relevantStandard",
    description: "relevantStandardDesc",
    isCompliant: true,
  },
  {
    name: "validityPeriod",
    description: "validityPeriodDesc",
    isCompliant:
      tcIssueDateInScValidityPeriod(transactionCertificate, scopeCertificate) ||
      false,
  },
  {
    name: "certifiedEntity",
    description: "certifiedEntityDesc",
    isCompliant: certificatesOnSameLegalEntity(
      transactionCertificate,
      scopeCertificate
    ),
  },
  {
    name: "productCategories",
    description: "productCategoriesDesc",
    isCompliant: tcProductCategoryInScProductCategories(
      transactionCertificate,
      scopeCertificate
    ),
  },
  {
    name: "certificationBody",
    description: "certificationBodyDesc",
    isCompliant:
      tcAndScFromSameCertificationBody(
        transactionCertificate,
        scopeCertificate
      ) || false,
  },
]
